<script setup>
import { computed } from 'vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
    // [{ date: 'YYYY-MM-DD', count: number }]
  },
  height: {
    type: Number,
    default: 160,
  },
});

const WIDTH = 800;
const PAD = { top: 16, right: 8, bottom: 32, left: 32 };
const chartW = computed(() => WIDTH - PAD.left - PAD.right);
const chartH = computed(() => props.height - PAD.top - PAD.bottom);

const maxVal = computed(() => {
  const m = Math.max(...props.data.map(d => d.count), 1);
  return Math.ceil(m / 4) * 4;
});

const points = computed(() => {
  if (!props.data.length) return [];
  return props.data.map((d, i) => ({
    x: PAD.left + (i / Math.max(props.data.length - 1, 1)) * chartW.value,
    y: PAD.top + (1 - d.count / maxVal.value) * chartH.value,
    date: d.date,
    count: d.count,
  }));
});

const linePath = computed(() => {
  if (points.value.length < 2) return '';
  let d = `M ${points.value[0].x} ${points.value[0].y}`;
  for (let i = 1; i < points.value.length; i++) {
    const p = points.value[i - 1];
    const c = points.value[i];
    const cpx = (p.x + c.x) / 2;
    d += ` C ${cpx} ${p.y} ${cpx} ${c.y} ${c.x} ${c.y}`;
  }
  return d;
});

const areaPath = computed(() => {
  if (!linePath.value) return '';
  const bottom = PAD.top + chartH.value;
  const first = points.value[0];
  const last = points.value[points.value.length - 1];
  return `${linePath.value} L ${last.x} ${bottom} L ${first.x} ${bottom} Z`;
});

const gridLines = computed(() => {
  const lines = [];
  const steps = 4;
  for (let i = 0; i <= steps; i++) {
    const y = PAD.top + (i / steps) * chartH.value;
    const val = Math.round(maxVal.value * (1 - i / steps));
    lines.push({ y, val });
  }
  return lines;
});

const xLabels = computed(() => {
  if (!props.data.length) return [];
  const step = Math.ceil(props.data.length / 6);
  return props.data
    .filter((_, i) => i % step === 0 || i === props.data.length - 1)
    .map((d, _, arr) => {
      const idx = props.data.indexOf(d);
      const x = PAD.left + (idx / Math.max(props.data.length - 1, 1)) * chartW.value;
      const parts = d.date.split('-');
      const label = `${parseInt(parts[2])} de ${['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'][parseInt(parts[1]) - 1]}.`;
      return { x, label };
    });
});
</script>

<template>
  <div class="w-full overflow-hidden">
    <svg
      :viewBox="`0 0 ${WIDTH} ${height}`"
      preserveAspectRatio="none"
      class="w-full"
      :style="{ height: height + 'px' }"
    >
      <defs>
        <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#2dd4bf" stop-opacity="0.3" />
          <stop offset="100%" stop-color="#2dd4bf" stop-opacity="0" />
        </linearGradient>
      </defs>

      <!-- Grid lines -->
      <g>
        <line
          v-for="(line, i) in gridLines"
          :key="i"
          :x1="PAD.left"
          :y1="line.y"
          :x2="WIDTH - PAD.right"
          :y2="line.y"
          stroke="currentColor"
          stroke-width="0.5"
          class="text-n-slate-10/20"
        />
        <text
          v-for="(line, i) in gridLines"
          :key="`label-${i}`"
          :x="PAD.left - 4"
          :y="line.y + 4"
          text-anchor="end"
          font-size="10"
          class="fill-n-slate-10"
        >{{ line.val }}</text>
      </g>

      <!-- Area fill -->
      <path
        v-if="areaPath"
        :d="areaPath"
        fill="url(#chartGradient)"
      />

      <!-- Line -->
      <path
        v-if="linePath"
        :d="linePath"
        fill="none"
        stroke="#2dd4bf"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />

      <!-- X-axis labels -->
      <g>
        <text
          v-for="(label, i) in xLabels"
          :key="`x-${i}`"
          :x="label.x"
          :y="PAD.top + chartH + 20"
          text-anchor="middle"
          font-size="10"
          class="fill-n-slate-10"
        >{{ label.label }}</text>
      </g>
    </svg>
  </div>
</template>
